import path from 'node:path';
import { ATTR_HTTP_ROUTE } from '@opentelemetry/semantic-conventions';
import * as Sentry from '@sentry/nextjs';
import Bubble from 'components/bubble';
import { DriveRollup } from 'components/cards';
import Counter from 'components/counter';
import HexMap from 'components/HexMap';
import Layout from 'components/layout';
import Anchor from 'components/typography/anchor';
import driveShareImg from 'images/shares/drives.png';
import { getDriveAccumulation } from 'lib/api/themis/queries';
import { otelApi } from 'lib/instrumentation'; // Initializes instrumentation
import { driveRollupToUserContentSlim } from 'lib/transformers';
import PropTypes from 'prop-types';
import { DATA_TYPES } from 'siteconfig';
import slugify from 'slugify';
import { CampaignTypeDriveRollupList } from 'utilities/propTypes';
import { isValidStateCode } from 'utilities/states';

const REVALIDATE_TIME_MINUTES = 1;
const REVALIDATE_TIME_SECONDS = REVALIDATE_TIME_MINUTES * 60;

export const getStaticProps = Sentry.wrapGetStaticPropsWithSentry(async () => {
  const parentCtx = otelApi.context.active();
  const parentSpan = otelApi.trace
    .getTracer('Gin Rickey')
    .startSpan('drives', undefined, parentCtx);

  parentSpan.setAttributes({
    [ATTR_HTTP_ROUTE]: `/drives`,
  });

  const dataCandidate =
    (await getDriveAccumulation({
      driveType: 'CANDIDATE',
    })) || {};

  // This returns some excessivly high totals, so lets rely on the state addition for now
  const totalSigns = dataCandidate?.totalSigns || 0;
  const candidates = dataCandidate?.candidates || [];
  const signData = dataCandidate?.stateTotals || [];

  const candidatesRollup = [];
  const signsPerState = {};
  let highestStateCount = 0;

  // We have to iterate on this again client side but let's get as much work
  // done here first so it's less for the client to do.
  signData.forEach((s) => {
    if (isValidStateCode(s.districtState)) {
      const signCountForState = s.total;
      signsPerState[s.districtState] = signCountForState;

      // Track the highest count
      if (signCountForState > highestStateCount) {
        highestStateCount = signCountForState;
      }
    }
  });

  candidates.forEach((c) => {
    // Early drives had screwed up states, ignore them here for now
    // Could be removed once we deal with those early Demings/Beto drives
    // That have incorrect states on them in TS
    if (isValidStateCode(c.districtState)) {
      candidatesRollup.push(
        driveRollupToUserContentSlim({ apiDriveRollup: c })
      );
    }
  });
  // Highest to lowest
  candidatesRollup.sort((a, b) => b.signCount - a.signCount);

  parentSpan.end();
  return {
    props: {
      candidates: candidatesRollup,
      totalSigns: totalSigns,
      signsPerState,
      highestStateCount,
    },
    revalidate: REVALIDATE_TIME_SECONDS,
  };
});

const Drives = ({
  candidates,
  totalSigns,
  signsPerState,
  highestStateCount,
}) => {
  const candidateSection = candidates.map((c) => (
    <DriveRollup
      key={c.candidateId}
      driveRollup={c}
      linkPath={path.join(
        DATA_TYPES.CANDIDATE.basePath,
        `${c.candidateId}/${slugify(c.candidateName)}`
      )}
      type={DATA_TYPES.CANDIDATE}
    />
  ));

  return (
    <Layout
      title="Vote Drives"
      description="Vote drives created by Resistbot members."
      image={driveShareImg}
      imageAltText='Illustration of a bot voting with overlay text that reads, "Vote Drives: Endorse your candidate and get out the vote before Election Day. Text drive to 50409.'
    >
      <div className="flex flex-row md:flex-col md:text-center">
        <div className="flex-1 md:mr-9">
          <h1>Drives</h1>
          <p className="text-sm">
            Here are the candidate receiving the most pledges so far. Text{' '}
            <Bubble>drive</Bubble> to the bot to create your own{' '}
            <Anchor href="/drives">drive</Anchor> to register and get your
            friends to the polls!
          </p>
        </div>
        <div>
          <Counter count={totalSigns} text="Total Pledges" />
        </div>
      </div>

      <HexMap
        states={signsPerState}
        highestStateCount={highestStateCount}
        basePath={DATA_TYPES.DRIVE.basePath}
      />

      {/* Currently this displays _all_ candidates we have drives for. */}
      {candidateSection}
    </Layout>
  );
};

Drives.propTypes = {
  candidates: PropTypes.arrayOf(CampaignTypeDriveRollupList),
  totalSigns: PropTypes.number.isRequired,
  signsPerState: PropTypes.objectOf(PropTypes.number),
};

export default Drives;
